// Busca UN equipo en el servidor, sin depender de las páginas cargadas: por IMEI exacto
// (`GET /backoffice/devices?imei=`) o por el detalle del crédito (`GET /backoffice/devices/{id}`).
import { useMemo } from 'react';
import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { CREDIT_ID_PATTERN } from '@fiado/type-kit/bin/common/constants/CreditIdPattern';
import { mdmBackofficeApi } from '@/api/services/loan-credit';
import type { LoanDeviceFleetItem } from '@/api/services/loan-credit/contract';
import { userMessageOf } from '@/api/errors';
import { IMEI_PATTERN } from '@/lib/imei';
import { fetchCreditDevice } from '../../creditFile/api/useCreditFile';
import type { MdmFleetDevice } from '../types';
import { toFleetDevice } from './mappers';
import { CREDIT_FILE_DEVICE_KEY, MDM_FLEET_KEY } from './queryKeys';

const STALE_TIME_MS = 30_000;

export type MdmDeviceLookupState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'error'; message: string; error: Error }
    /** Lo que contestó el servidor. Vacío = no existe, no «no se cargó». */
    | { status: 'ready'; devices: readonly MdmFleetDevice[] };

export interface MdmDeviceLookupTarget {
    imei?: string;
    /** El `creditId`; manda el `imei` cuando vienen los dos. */
    credit?: string;
}

const IDLE: MdmDeviceLookupState = { status: 'idle' };
const LOADING: MdmDeviceLookupState = { status: 'loading' };
const NOTHING: MdmDeviceLookupState = { status: 'ready', devices: [] };

/** La clave de la búsqueda por IMEI: cuelga de la flota para que las acciones la invaliden. */
export function imeiLookupKey(imei: string): readonly unknown[] {
    return [...MDM_FLEET_KEY, 'imei', imei];
}

/** El ULID en mayúsculas, o `undefined` si el texto no puede ser un crédito. */
export function creditIdOf(text: string | undefined): string | undefined {
    const upper = text?.trim().toUpperCase();
    return upper && CREDIT_ID_PATTERN.test(upper) ? upper : undefined;
}

/** El equipo de un IMEI o de un crédito, según el servidor. */
export function useMdmDeviceLookup(
    target: MdmDeviceLookupTarget,
    enabled: boolean,
): MdmDeviceLookupState {
    const imei = target.imei?.trim() || undefined;
    const validImei = imei !== undefined && IMEI_PATTERN.test(imei) ? imei : undefined;
    const creditId = imei === undefined ? creditIdOf(target.credit) : undefined;
    const hasCredit = imei === undefined && (target.credit?.trim() ?? '') !== '';

    const byImei = useQuery({
        queryKey: imeiLookupKey(validImei ?? ''),
        queryFn: () => mdmBackofficeApi.listMdmDevices({ imei: validImei }),
        enabled: enabled && validImei !== undefined,
        staleTime: STALE_TIME_MS,
        retry: false,
        refetchOnWindowFocus: false,
    });

    // Misma clave y misma lectura que la ficha del crédito: comparten caché e invalidación.
    const byCredit = useQuery({
        queryKey: [...CREDIT_FILE_DEVICE_KEY, creditId ?? ''],
        queryFn: () => fetchCreditDevice(creditId ?? ''),
        enabled: enabled && creditId !== undefined,
        staleTime: STALE_TIME_MS,
        retry: false,
        refetchOnWindowFocus: false,
    });

    const imeiDevices = useMemo(() => byImei.data?.items.map(toFleetDevice), [byImei.data]);
    const creditDevices = useMemo(
        () =>
            byCredit.data === undefined
                ? undefined
                : byCredit.data
                  ? [toFleetDevice(byCredit.data)]
                  : [],
        [byCredit.data],
    );

    return useMemo(() => {
        if (!enabled) return IDLE;
        if (imei !== undefined) {
            // Lo que no son 15 dígitos no es un IMEI: ningún equipo lo tiene.
            if (validImei === undefined) return NOTHING;
            if (byImei.error)
                return {
                    status: 'error',
                    message: userMessageOf(byImei.error),
                    error: byImei.error,
                };
            return imeiDevices ? { status: 'ready', devices: imeiDevices } : LOADING;
        }
        if (!hasCredit) return IDLE;
        // Lo que no es ULID no es un `creditId`: el expediente MDM no guarda otro folio.
        if (creditId === undefined) return NOTHING;
        if (byCredit.error)
            return {
                status: 'error',
                message: userMessageOf(byCredit.error),
                error: byCredit.error,
            };
        return creditDevices ? { status: 'ready', devices: creditDevices } : LOADING;
    }, [
        enabled,
        imei,
        validImei,
        byImei.error,
        imeiDevices,
        hasCredit,
        creditId,
        byCredit.error,
        creditDevices,
    ]);
}

/** Los equipos de varios créditos, leídos por su detalle: sin páginas, nada queda fuera. */
export interface MdmDevicesOfCredits {
    devices: readonly MdmFleetDevice[];
    isLoading: boolean;
    /** La primera lectura que falló. Un crédito sin equipo NO es un fallo: no aporta fila. */
    error: Error | null;
}

/** Fuera del componente para que react-query memoice el resultado entre renders. */
function combineCreditDevices(
    results: UseQueryResult<LoanDeviceFleetItem | null>[],
): MdmDevicesOfCredits {
    return {
        devices: results.flatMap((result) => (result.data ? [toFleetDevice(result.data)] : [])),
        isLoading: results.some((result) => result.isLoading),
        error: results.find((result) => result.error)?.error ?? null,
    };
}

/** El equipo de cada crédito, en paralelo y con la misma caché que la ficha del crédito. */
export function useMdmDevicesOfCredits(creditIds: readonly string[]): MdmDevicesOfCredits {
    return useQueries({
        queries: creditIds.map((creditId) => ({
            queryKey: [...CREDIT_FILE_DEVICE_KEY, creditId],
            queryFn: () => fetchCreditDevice(creditId),
            staleTime: STALE_TIME_MS,
            retry: false,
            refetchOnWindowFocus: false,
        })),
        combine: combineCreditDevices,
    });
}
